import { Columns } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/BasicTable';
import {
  MemberClaims,
  collapsiblePatientTableHeaderStyles,
} from './riskPatientDetailsHelper';
import { numFormatterWithSuffix, numberToCurrencyFormatter } from './helper';
import { CostValues } from './summaryReportHelper';
import properties from '../properties';
import axios from 'axios';
import { formatDate } from './helper';
import moment from 'moment';

export interface CostTrendData {
  preOperative: { value: number }[];
  surgicalCost: { value: number }[];
  postOperative: { value: number }[];
  xAxisValues: {
    label: string;
  }[];
  quantile25: string;
  quantile75: string;
  median: string;
}

export interface CostbreakDownData {
  preOperative: number;
  surgicalCost: number;
  postOperative: number;
  totalCost: number;
}

export interface EpisodesValues {
  episodeCount: string;
  claimsCount: string;
  opportunityCost: string;
  cohortCost?: string;
}

export const collapsibleMemberClaimsTableColumns: Columns[] = [
  {
    name: 'Member ID',
    align: 'left',
    cellStyles: collapsiblePatientTableHeaderStyles,
    typographyStyles: collapsiblePatientTableHeaderStyles,
    width: '280px',
    cellPadding: '8px 18px 8px 56px',
  },
  {
    name: 'Total Cost',
    align: 'right',
    cellStyles: collapsiblePatientTableHeaderStyles,
    typographyStyles: collapsiblePatientTableHeaderStyles,
    width: '254px',
    cellPadding: '8px 220px 8px 20px',
  },
  {
    name: 'Duration',
    align: 'left',
    cellStyles: collapsiblePatientTableHeaderStyles,
    typographyStyles: collapsiblePatientTableHeaderStyles,
    width: '260px',
    cellPadding: '8px 16px 8px 20px',
  },
  {
    name: 'Variation from Median',
    align: 'right',
    cellStyles: collapsiblePatientTableHeaderStyles,
    typographyStyles: collapsiblePatientTableHeaderStyles,
    width: '200px',
    cellPadding: '8px 60px 8px 20px',
  },
];

export interface MemberClaimDetails {
  member_unique_id: string;
  total_cost: number;
  duration: string;
  variance: string;
  claimData: MemberClaims[];
}

export const formatEocDetails = (data: any) => {
  let tableData = [];
  //adding filter to not consider objects with 0 episode_cost for now
  tableData = data
    ?.filter((item: any) => item?.episode_cost !== 0)
    ?.map((item: any) => {
      return {
        episodeName: item?.episode,
        episodes: item?.member_count,
        totalCost: item?.episode_cost,
        percentile25: item['25th_per_over'],
        medianCost: item['50th_per_over'],
        percentile75: item['75th_per_over'],
        surgeryCost:
          item?.group_events?.find(
            (data: any) => data?.final_event_type === 'EVENT',
          )?.final_event_value || 0,
        preOperative:
          item?.group_events?.find(
            (data: any) => data?.final_event_type === 'PRE',
          )?.final_event_value || 0,
        postOperative:
          item?.group_events?.find(
            (data: any) => data?.final_event_type === 'POST',
          )?.final_event_value || 0,
      };
    });
  return tableData;
};

export const formatEocCostValues = (medical: any, rx: any) => {
  const costValues: CostValues = {
    medical: '',
    rx: '',
    total: '',
  };
  costValues.medical = numberToCurrencyFormatter(medical, true);
  costValues.rx = numberToCurrencyFormatter(rx, true);
  costValues.total = numberToCurrencyFormatter(medical + rx, true);
  return costValues;
};

export const formatEpisodeValue = (
  claimsCount: any,
  episodeCount: any,
  opportunityCost: any,
  cohortCost?: any,
) => {
  const episodesValues: EpisodesValues = {
    claimsCount: '',
    episodeCount: '',
    opportunityCost: '',
    cohortCost: '',
  };
  episodesValues.claimsCount = claimsCount;
  episodesValues.episodeCount = episodeCount;
  episodesValues.opportunityCost =
    '$' + numFormatterWithSuffix(opportunityCost, 2);
  episodesValues.cohortCost = '$' + numFormatterWithSuffix(cohortCost, 2);
  return episodesValues;
};
export interface TreatmentCostBreakdown {
  costType: string;
  totalCost: number;
  percentile25: string;
  medianCost: string;
  percentile75: string;
  subType: {
    costType: string;
    totalCost: number;
    percentile25: string;
    medianCost: string;
    percentile75: string;
  }[];
}

const getOperativeName = (name: string) => {
  switch (name) {
    case 'PRE':
      return 'Pre Operative';
    case 'EVENT':
      return 'Surgery';
    case 'POST':
      return 'Post Operative';
    default:
      return name;
  }
};

export const formatTreatmentCostBreakdown = (
  data: any,
): TreatmentCostBreakdown[] => {
  const groupEventsData = data[0].group_events;
  const treatmentCostBreakdownData: TreatmentCostBreakdown[] = [];
  const sortOrder = ['PRE', 'EVENT', 'POST'];
  groupEventsData?.map((item: any) => {
    const index = sortOrder.indexOf(item.final_event_type);
    const eachEventTypeData: any = {
      costType: getOperativeName(item.final_event_type),
      totalCost: item.total_paid_amount,
      percentile25: item['25th_per'],
      medianCost: item['50th_per'],
      percentile75: item['75th_per'],
      subType: [],
    };
    item.event_group?.map((groupItem: any) => {
      const eachSubEventData = {
        costType: groupItem.cpt_group,
        totalCost: groupItem.cost,
        percentile25: groupItem['25th_per'],
        medianCost: groupItem['50th_per'],
        percentile75: groupItem['75th_per'],
      };

      eachEventTypeData.subType?.push(eachSubEventData);
    });
    treatmentCostBreakdownData?.splice(index, 0, eachEventTypeData);
  });
  return treatmentCostBreakdownData;
};

export const getMemberClaims = async (requestBody: any) => {
  const res = await axios.post(
    properties.aiqBaseUrl +
      properties.membersBaseUrl +
      properties.memberDetails,
    requestBody,
  );

  if (res?.status === 200) {
    return res?.data?.data;
  }

  return [];
};

export const getQualityMeasures = async (
  startDate: string,
  eg_nid: string[],
  episode: string,
  member_unique_ids: string[],
  setQualityMeasures: any,
) => {
  await axios
    .post(
      properties.aiqBaseUrl +
        properties.episodesOfCare +
        properties.qualityMeasures,
      {
        service_year: moment(startDate).year().toString(),
        eg_nid: eg_nid,
        episode: episode,
        member_unique_ids: member_unique_ids,
      },
    )
    .then(async (res) => {
      if (res?.status === 200) {
        setQualityMeasures(res?.data?.data);
      }
    })
    .catch((error) => {
      return error;
    });
};

export const formatMemberDetails = async (
  data: any,
  startDate: string,
  endDate: string,
  eg_nid: string[],
   
  setQualityMeasures: any,
   
  episode: string,
): Promise<MemberClaimDetails[]> => {
  const episodeMemberData = data.mem_details;
  const memberDetails: MemberClaimDetails[] = [];
  const member_unique_ids: string[] = [];

  for (const item of episodeMemberData) {
    member_unique_ids.push(item.member_unique_id);

    const eachMemberDetails: MemberClaimDetails = {
      member_unique_id: item.member_unique_id,
      total_cost: item.total_paid_amount,
      variance: numberToCurrencyFormatter(item.variation),
      duration:
        formatDate(item.start_date, 'MMM YYYY') +
        ' - ' +
        formatDate(item.end_date, 'MMM YYYY'),
      claimData: [],
    };

    const claimData = await getMemberClaims({
      eg_nid: eg_nid,
      startDate: startDate,
      endDate: endDate,
      member_unique_id: [item.member_unique_id],
      limit: 20,
      increment: 0,
      claim_id_list: item.claim_id,
    });

    eachMemberDetails.claimData = claimData;

    memberDetails.push(eachMemberDetails);
  }

  // getQualityMeasures(
  //   startDate,
  //   eg_nid,
  //   episode,
  //   member_unique_ids,
  //   setQualityMeasures,
  // );

  return memberDetails;
};

export const formatCostTrendData = (data: any): CostTrendData => {
  const preOperative: { value: number; displayValue: string }[] = [];
  const surgicalCost: { value: number; displayValue: string }[] = [];
  const postOperative: { value: number; displayValue: string }[] = [];
  const xAxisValues: { label: string }[] = [];
  const quantile25: string = `${data['25th_per_over']}`;
  const quantile75: string = `${data['75th_per_over']}`;
  const median: string = `${data['50th_per_over']}`;
  data?.mem_details?.map((costTrend: any) => {
    preOperative.push({
      value: costTrend?.pre_cost,
      displayValue: `Member ID - ${
        costTrend?.member_unique_id
      } <br> Duration - ${costTrend?.start_date} to ${
        costTrend?.end_date
      } <br> Total Cost - ${numberToCurrencyFormatter(
        costTrend?.total_paid_amount,
        true,
        2,
      )} <br>
      <div style="border-top: 1px solid #ccc;
      margin-top: 10px;
      margin-bottom: 10px;"></div>
       Pre Operative - ${numberToCurrencyFormatter(
         costTrend?.pre_cost,
         true,
         2,
       )} <br> Surgery Cost - ${numberToCurrencyFormatter(
        costTrend?.event_cost,
        true,
        2,
      )} <br> Post Operative - ${numberToCurrencyFormatter(
        costTrend?.post_cost,
        true,
        2,
      )}`,
    });
    surgicalCost.push({
      value: costTrend?.event_cost,
      displayValue: `Member ID - ${
        costTrend?.member_unique_id
      } <br> Duration - ${costTrend?.start_date} to ${
        costTrend?.end_date
      } <br> Total Cost - ${numberToCurrencyFormatter(
        costTrend?.total_paid_amount,
        true,
        2,
      )} <br>
      <div style="border-top: 1px solid #ccc;
      margin-top: 10px;
      margin-bottom: 10px;"></div>
       Pre Operative - ${numberToCurrencyFormatter(
         costTrend?.pre_cost,
         true,
         2,
       )} <br> Surgery Cost - ${numberToCurrencyFormatter(
        costTrend?.event_cost,
        true,
        2,
      )} <br> Post Operative - ${numberToCurrencyFormatter(
        costTrend?.post_cost,
        true,
        2,
      )}`,
    });
    postOperative.push({
      value: costTrend?.post_cost,
      displayValue: `Member ID - ${
        costTrend?.member_unique_id
      } <br> Duration - ${costTrend?.start_date} to ${
        costTrend?.end_date
      } <br> Total Cost - ${numberToCurrencyFormatter(
        costTrend?.total_paid_amount,
        true,
        2,
      )} <br>
      <div style="border-top: 1px solid #ccc;
      margin-top: 10px;
      margin-bottom: 10px;"></div>
       Pre Operative - ${numberToCurrencyFormatter(
         costTrend?.pre_cost,
         true,
         2,
       )} <br> Surgery Cost - ${numberToCurrencyFormatter(
        costTrend?.event_cost,
        true,
        2,
      )} <br> Post Operative - ${numberToCurrencyFormatter(
        costTrend?.post_cost,
        true,
        2,
      )}`,
    });
    xAxisValues.push({
      label: costTrend?.x_Axis_Label,
    });
  });

  return {
    preOperative,
    surgicalCost,
    postOperative,
    xAxisValues,
    quantile25,
    median,
    quantile75,
  };
};

export const formatCostBreakDownData = (data: any) => {
  const costBreakDownData: CostbreakDownData = {
    preOperative: 0,
    surgicalCost: 0,
    postOperative: 0,
    totalCost: 0,
  };

  if (!(data.length === 0)) {
    data[0]?.group_events?.map((groupEvent: any) => {
      if (groupEvent?.final_event_type === 'POST') {
        costBreakDownData.postOperative = groupEvent?.total_paid_amount;
      } else if (groupEvent?.final_event_type === 'PRE') {
        costBreakDownData.preOperative = groupEvent?.total_paid_amount;
      } else if (groupEvent?.final_event_type === 'EVENT') {
        costBreakDownData.surgicalCost = groupEvent?.total_paid_amount;
      }
    });
    costBreakDownData.totalCost = data[0]?.episode_cost;
  }

  return costBreakDownData;
};
